import type { CartLineUpdateInput, MailingAddressInput } from "@shopify/hydrogen-react/storefront-api-types";
import type { TossPaymentsWidgets, WidgetPaymentMethodWidget } from "@tosspayments/tosspayments-sdk";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { t } from "i18next";
import type { ReactNode } from "react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { ICalculatedDraftOrder } from "@repo/shopify/api/calculateDraftOrderV2";
import calculateDraftOrderV2 from "@repo/shopify/api/calculateDraftOrderV2";
import { createDraftOrder } from "@repo/shopify/api/createDraftOrder";
import useCartInfo from "@repo/shopify/hooks/useCartInfo";
import useCustomerInfo from "@repo/shopify/hooks/useCustomerInfo";
import { getStoredGoogleCampaignId, getStoredGoogleTrafficSource } from "@repo/shopify/hooks/useGoogleTrafficSource";
import { getStoredMetaCampaignId, getStoredMetaTrafficSource } from "@repo/shopify/hooks/useMetaTrafficSource";
import useTracking from "@repo/shopify/hooks/useTracking";
import type { ICart, ICartEdge } from "@repo/shopify/queries/cart-gql";
import type { TIntlAddressForm } from "@repo/shopify/types/address";
import { findCodes, isValidAddress } from "@repo/shopify/utils/address";
import { checkCountryLimitProducts, checkQuantityLimit } from "@repo/shopify/utils/cart";
import { extractId } from "@repo/shopify/utils/common";
import { formatPhone } from "@repo/shopify/utils/format";
import { RESIDENT_COUNTRY, checkResidentId } from "@repo/shopify/utils/order";

import { createDraftOrderAsia, fetchCancelPayment } from "@api/lambdaApi";

import addressFormData from "@assets/addressForms.json";

import Button from "@atoms/Button";
import Input from "@atoms/Input";
import Loading from "@atoms/Loading";
import Price from "@atoms/Price";

import CheckoutLineItem from "@molecules/CheckoutLineItem";
import Discounts from "@molecules/Discounts";

import BillingAddressIntl, { type IBillingAddressIntlHandle } from "@organisms/Address/BillingAddressIntl";
import ShippingAddress from "@organisms/Address/ShippingAddress";

import { INTL_ASIA_PROVIDER } from "@const/constant";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCartQuery from "@hooks/useCartQuery";
import useCheckLocation from "@hooks/useCheckLocation";
import useCustomerQuery from "@hooks/useCustomerQuery";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

interface IFormData {
  discountCode: string;
}

const CLIENT_KEY = import.meta.env.VITE_REACT_APP_TOSS_CLIENT_KEY as string;
const TEST_CLIENT_KEY = import.meta.env.VITE_REACT_APP_TOSS_TEST_CLIENT_KEY as string;

const getPayParamKey = (orderId: string) => `TINT_INTL_PAY_PARAM_${orderId}`;
const getCardMethodKey = (orderId: string) => `CARD_METHOD_${orderId}`;

// 결제 데이터 정리
const clearPaymentData = ({ ttl, orderId }: { ttl?: number; orderId?: string } = {}) => {
  const now = Date.now();

  // 1. orderId가 있는 경우 해당 데이터만 삭제
  if (orderId) {
    localStorage.removeItem(getPayParamKey(orderId));
    localStorage.removeItem(getCardMethodKey(orderId));
    return;
  }

  // 2. 로컬 스토리지에서 모든 결제 관련 키를 필터링
  const paymentKeys = Object.keys(localStorage).filter((key) => key.startsWith("TINT_INTL_PAY_PARAM_") || key.startsWith("CARD_METHOD_"));

  paymentKeys.forEach((key) => {
    // 3-1. TTL 없는 경우 모든 데이터 삭제
    if (ttl === undefined) {
      localStorage.removeItem(key);
      return;
    }

    // 3-2. TTL 있는 경우 만료된 데이터만 삭제
    const timestamp = Number(key.split("_").pop());
    const isExpired = !isNaN(timestamp) && now - timestamp > ttl;

    if (isExpired) {
      localStorage.removeItem(key);
    }
  });
};

const MODAL_TITLE: Record<string, string> = {
  checkoutError: "Checkout Error",
  addressError: "Shipping Address",
  paymentError: "Payment Error",
  overQuantity: "Quantity Limit",
  invalidCart: "Checkout Error",
};

export default function CheckoutIntl() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { cart, isSyncing } = useCartStore();
  const { customer, token, logout } = useCustomerStore();
  const { openToast, openModal, openAccount, setIsLoading: setIsModalLoading } = useLayoutStore();

  const { fetchCustomer } = useCustomerQuery();
  const { setBuyerIdentity, setBuyerIdentityLoading, updateCartLine, updateCartLineLoading } = useCartQuery();

  const { isProd } = useCheckLocation();
  const { gtmBeginCheckout, gtmAddShippingInfo, gtmAddPaymentInfo } = useTracking();

  const [isInitLoading, setIsInitLoading] = useState(true); // 초기 로딩
  const [isLoading, setIsLoading] = useState(false); // 주문 생성 로딩
  const [isPaymentLoading, setIsPaymentLoading] = useState(false); // 결제 요청 로딩
  const [isPaymentWidgetLoading, setIsPaymentWidgetLoading] = useState(true); // 위젯 로딩
  const [isDiscountCodeApplied, setIsDiscountCodeApplied] = useState(false); // 할인 코드 적용 여부
  const [isDiscountLoading, setIsDiscountLoading] = useState<boolean>(false); // 할인코드 적용 버튼 로딩
  const [isCalculationLoading, setIsCalculationLoading] = useState<boolean>(true); // draftOrderCalculate api 버튼 로딩

  const [calculatedOrder, setCalculatedOrder] = useState<ICalculatedDraftOrder>();
  const [selectedAddressId, setSelectedAddressId] = useState(customer?.defaultAddress?.id || ""); // 선택된 주소 ID
  const [isBillingAsShipping, setIsBillingAsShipping] = useState<boolean>(true); // 청구 주소가 배송 주소와 같은지 여부
  const [residentId, setResidentId] = useState("");
  const [isAvailableResidentId, setIsAvailableResidentId] = useState(true);

  const tossPaymentsRef = useRef<TossPaymentsWidgets | null>(null);
  const tossPaymentsWidgetRef = useRef<WidgetPaymentMethodWidget | null>(null);
  const billingHandleRef = useRef<IBillingAddressIntlHandle>(null);
  const billingAddressRef = useRef<MailingAddressInput | undefined>();
  const initStartedRef = useRef(false);

  const {
    originTotalAmount,
    autoDiscountAmount,
    autoDiscountTitle,
    codeDiscountTitle,
    codeDiscountAmount,
    totalAmount,
    taxAmount,
    dutyAmount,
    shippingTitle,
    shippingAmount,
  } = useCartInfo(cart, calculatedOrder);

  const { isEmployee, membership, isMembershipDiscountCodeUsed, addresses, codeUsableMembership } = useCustomerInfo(
    customer,
    "thisisneverthat",
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: { discountCode: "" },
    mode: "onChange",
  });

  const { discountCode } = watch();

  /** 선택된 배송지 주소 */
  const selectedAddress = useMemo(
    () => addresses?.find((address) => extractId(address.id) === extractId(selectedAddressId)),
    [addresses, selectedAddressId],
  );

  /** 배송 불가능한 상품 IDs */
  const limitedProductIds = useMemo(() => {
    if (!selectedAddress) return [];
    const limitedProducts = checkCountryLimitProducts(cart, selectedAddress);
    return limitedProducts.map(({ node }: { node: ICartEdge["node"] }) => node.merchandise.product.id);
  }, [cart, selectedAddress]);

  useEffect(() => {
    if (isSyncing || initStartedRef.current) return;
    initStartedRef.current = true;

    void init();
  }, [isSyncing]);

  // 카트에 적용되어 있는 할인코드 유효성 검사
  useEffect(() => {
    // 토스 결제 이후 비워지는 카트 or 결제 진행 중 카트 조정은 검사하지 않음
    if (isLoading || isPaymentLoading || isInitLoading) return;

    if (!cart.lines.edges.length) {
      openToast(NOTIFICATIONS.WARNING_NO_CART);
      navigate(URLS.HOME);
    }
  }, [isLoading, isPaymentLoading, isInitLoading, cart.lines.edges.length]);

  // 주소지 변경 시 buyerIdentity 업데이트
  useEffect(() => {
    if (!extractId(selectedAddressId)) {
      setCalculatedOrder(undefined);
      // 계산이 돌지 않는 경로다. 로딩을 남기면 배송지 추가/변경 버튼까지 잠겨 주소를 넣을 수가 없다
      setIsCalculationLoading(false);
      return;
    }

    void handleBuyerIdentity();
  }, [extractId(selectedAddressId), selectedAddress?.country, selectedAddress?.address1]);

  /** 초기화 함수*/
  const init = async (providedCart?: ICart["cart"]) => {
    try {
      const isSearchParams = await handleSearchParams();
      if (isSearchParams) return;

      // 로컬스토리지 결제 데이터 정리
      clearPaymentData();

      const isCustomerValid = await checkCustomer();
      if (!isCustomerValid) return;

      let currentCart = providedCart || cart;

      // adjustCartLineItems 함수 내부에서 새로 불러오는 카트에 갯수가 0개인 상품이 있을 경우 한번 더 호출됨
      currentCart = await adjustCartLineItems(currentCart);

      gtmBeginCheckout(currentCart, customer.id ? extractId(customer.id) : undefined, customer.email ?? undefined);

      if (!currentCart.lines.edges.length) {
        handleValidationModal({ type: "invalidCart", content: t("warning.invalid_cart") });
        return;
      }

      await initializeTossWidget();
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      navigate(URLS.HOME);
      setIsInitLoading(false);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      setIsInitLoading(false);
    }
  };

  /** 초기화 함수 customer 유효성 검사 */
  const checkCustomer = async () => {
    // 로그인 하지 않은 경우
    if (!token.accessToken) {
      openToast({ message: t("warning.end_session") });
      logout();
      navigate(URLS.HOME);
      setTimeout(() => {
        openAccount(true);
      }, 300);
      return false;
    }

    // 유효하지 않은 토큰 (소셜로그인 동시접속 등으로 인해 토큰 만료)
    const { data } = await fetchCustomer({ variables: { customerAccessToken: token.accessToken } });
    if (!data?.customer) {
      openToast({ message: t("warning.end_session") });
      navigate(URLS.HOME);
      logout();
      setTimeout(() => {
        openAccount(true);
      }, 300);
      return false;
    }

    return true;
  };

  /** 토스 승인 후 리다이렉트 파라미터 처리 */
  const handleSearchParams = async () => {
    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const amount = searchParams.get("amount");
    const errorMessage = searchParams.get("message");
    const code = searchParams.get("code");

    if (errorMessage) {
      handleValidationModal({ type: "paymentError", content: t("error.toss_payment_title") });
      return false;
    }

    if (paymentKey && orderId && amount) {
      const cardMethod = searchParams.get("cardMethod") || localStorage.getItem(getCardMethodKey(orderId));
      if (!cardMethod) {
        handleValidationModal({ type: "paymentError", content: t("warning.necessary_payment_data") });
        return false;
      }

      const storedParam = localStorage.getItem(getPayParamKey(orderId));
      const paymentParam = (() => {
        try {
          return storedParam ? (JSON.parse(storedParam) as ReturnType<typeof getLambdaPaymentParam>) : null;
        } catch {
          return null;
        }
      })();

      console.log("[THISISNEVERTHAT INTL] SEARCH PARAMS", {
        orderId,
        paymentKey,
        amount,
        cardMethod,
        cardMethodSource: searchParams.get("cardMethod") ? "URL" : "localStorage",
        errorMessage,
        code,
      });
      console.log("[THISISNEVERTHAT INTL] ORDER PAYMENT PARAM", paymentParam);
      await completePaymentRequest({ paymentKey, amount, orderId, cardMethod, paymentParam });
      return true;
    }

    return false;
  };

  /** 토스 위젯 초기화 + 설정 */
  const initializeTossWidget = async () => {
    try {
      setIsPaymentWidgetLoading(true);
      if (tossPaymentsWidgetRef.current) await tossPaymentsWidgetRef.current.destroy();

      const key = isProd ? CLIENT_KEY : TEST_CLIENT_KEY;
      const tossPayments = await loadTossPayments(key);

      tossPaymentsRef.current = tossPayments.widgets({ customerKey: extractId(customer.id) });
      await tossPaymentsRef.current.setAmount({ value: 9999, currency: "USD" });

      tossPaymentsWidgetRef.current = await tossPaymentsRef.current.renderPaymentMethods({
        selector: "#payment-method",
        variantKey: "TINT_INTL_default",
      });
    } catch (error) {
      console.error("[THISISNEVERTHAT INTL] Toss widget initialization failed", error);
      setIsPaymentWidgetLoading(false);
    } finally {
      setIsPaymentWidgetLoading(false);
    }
  };

  // 토스 결제
  const requestPaymentWidget = async (providedCart: ICart["cart"]) => {
    const lambdaPaymentParam = getLambdaPaymentParam(isDiscountCodeApplied ? calculatedOrder?.discountCodes?.[0] || "" : "", providedCart);
    if (!tossPaymentsRef.current || !tossPaymentsWidgetRef.current || !selectedAddress?.countryCodeV2 || !lambdaPaymentParam) {
      setIsPaymentLoading(false);
      return;
    }

    try {
      await tossPaymentsRef.current.setAmount({ value: Number(totalAmount), currency: "USD" });

      // UI Render 때문에 결제 요청 직전에 값 넣기
      const cardData = await tossPaymentsWidgetRef.current.getSelectedPaymentMethod();
      gtmAddPaymentInfo(providedCart, cardData.code, calculatedOrder, customer.id ? extractId(customer.id) : undefined);

      const isAsiaProvider = cardData.code !== "PAYPAL" && INTL_ASIA_PROVIDER.includes(cardData.code.toUpperCase());
      const redirectUrl = `${window.location.origin + window.location.pathname}?cardMethod=${encodeURIComponent(cardData.code)}`;
      const edges = providedCart.lines.edges;
      const countryCode = selectedAddress?.countryCodeV2 as string;

      const requestParam = {
        orderId: `${extractId(customer.id)}_${Number(new Date())}`,
        orderName:
          edges.length > 1 ? `${edges[0].node.merchandise.product.title}외 ${edges.length - 1}건` : edges[0].node.merchandise.product.title,
        // useInternationalCardOnly: true,
        customerName: customer.email || "",
        customerEmail: customer.email || "",
        successUrl: redirectUrl,
        failUrl: redirectUrl,
        ...(isAsiaProvider && {
          pendingUrl: redirectUrl,
          foreignEasyPay: {
            country: countryCode,
            products: [
              ...edges.map(({ node }) => ({
                name: node.merchandise.product.title,
                quantity: node.quantity,
                unitAmount: Number((Number(node.cost.totalAmount.amount) / node.quantity).toFixed(2)),
                currency: "USD",
                description: node.merchandise.title,
              })),
              {
                name: "Tax and Duties",
                quantity: 1,
                unitAmount: Number((taxAmount + dutyAmount).toFixed(2)),
                currency: "USD",
                description: "Tax and Duties",
              },
              {
                name: "Shipping Fee",
                quantity: 1,
                unitAmount: Number(shippingAmount.toFixed(2)),
                currency: "USD",
                description: "Shipping Fee",
              },
            ],
            shipping: {
              address: {
                country: countryCode,
                area2: selectedAddress?.city || "",
                postalCode: selectedAddress?.zip || "",
              },
            },
          },
        }),
      };

      // 아시아 간편결제를 위해 추가되어야 하는 주문 태그
      lambdaPaymentParam.orderData.tags = [
        ...(lambdaPaymentParam.orderData.tags ?? []),
        ...(isAsiaProvider ? ["asia_provider", `결제수단:${cardData.code}`] : []),
      ];

      console.log("[THISISNEVERTHAT INTL] LAMBDA PAYMENT PARAM", lambdaPaymentParam);
      console.log("[THISISNEVERTHAT INTL] REQUEST PARAM", requestParam);

      localStorage.setItem(getPayParamKey(requestParam.orderId), JSON.stringify(lambdaPaymentParam));
      localStorage.setItem(getCardMethodKey(requestParam.orderId), cardData.code);

      await tossPaymentsRef.current.requestPayment(requestParam);
    } catch (error: unknown) {
      // 사용자 취소 에러 처리
      if (error instanceof Error && "code" in error && (error as { code: string }).code === "USER_CANCEL") {
        handleValidationModal({
          type: "checkoutError",
          title: "Payment Canceled",
          content: t("warning.payment_cancel"),
        });
      } else {
        let message = error instanceof Error ? error.message : String(error);
        // 카드 결제수단 선택하지 않았을 때 에러 처리
        if ((error as { code: string }).code === "NEED_CARD_PAYMENT_DETAIL") {
          message = t("warning.necessary_payment_method");
        }
        // 기타 결제 에러 처리
        handleValidationModal({ type: "checkoutError", content: <p>{message}</p> });
        throw new Error(message);
      }
      setIsPaymentLoading(false);
    }
  };

  /** 토스 승인 후 주문 생성 요청 중 동남아 간편 결제 취소 핸들러 */
  const cancelPaymentHandler = async ({
    amount,
    paymentKey,
    orderId,
    reason,
  }: {
    amount: string;
    paymentKey: string;
    orderId: string;
    reason: string;
  }) => {
    console.log(`[THISISNEVERTHAT INTL] ASIA PAYMENT CANCEL: ${reason}`, { orderId });
    try {
      await fetchCancelPayment({ amount, paymentKey, orderId });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    }
  };

  /** 토스 승인 후 주문 생성 요청 */
  const completePaymentRequest = async ({
    paymentKey,
    orderId,
    amount,
    cardMethod,
    paymentParam,
  }: {
    paymentKey: string;
    orderId: string;
    amount: string;
    cardMethod: string;
    paymentParam?: ReturnType<typeof getLambdaPaymentParam>;
  }) => {
    setIsLoading(true);

    const isAsiaProvider = INTL_ASIA_PROVIDER.includes(cardMethod.toUpperCase());
    console.log("[THISISNEVERTHAT INTL] CARD METHOD CHECK", { cardMethod, isAsiaProvider });

    try {
      // 1. 파라미터 유효성 검증
      if (!paymentParam) {
        if (isAsiaProvider) await cancelPaymentHandler({ amount, paymentKey, orderId, reason: "MISSING_PARAM" });
        handleValidationModal({
          type: "paymentError",
          content: t("warning.necessary_payment_data"),
          orderId, // "paymentError" 타입일 때, 모달 콜백으로 clearPaymentData 호출해서 해당 orderId 데이터만 삭제하도록 함
        });
        setIsLoading(false);
        return;
      }

      // 2. 재고 검증
      const adjustedCart = await adjustCartLineItems(cart);
      if (!adjustedCart.lines.edges.length) {
        if (isAsiaProvider) await cancelPaymentHandler({ amount, paymentKey, orderId, reason: "OVERSELL" });
        handleValidationModal({ type: "invalidCart", content: t("warning.invalid_cart") });
        clearPaymentData({ orderId });
        // setIsLoading(false);
        return;
      }

      // 3. 주문 생성 요청
      const response = isAsiaProvider
        ? await createDraftOrderAsia({ paymentKey, orderId, amount: parseFloat(amount), ...paymentParam })
        : await createDraftOrder({
            shop: "thisisneverthat",
            domain: "en",
            param: { paymentKey, orderId, amount: parseFloat(amount), ...paymentParam },
            useTestClientKey: !isProd,
          });

      console.log("[THISISNEVERTHAT INTL] ORDER LAMBDA RESPONSE", response);

      // 4. 주문 성공 후처리
      if (response?.status === 200) {
        navigate(`/order-completed/${response.data.orderId}`, { replace: true });

        // 성공 후 로컬 스토리지에 남아있는 결제 데이터 삭제
        clearPaymentData({ orderId });
      } else {
        const errorMessage = response?.data?.message || t("error.payment");
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (isAsiaProvider) await cancelPaymentHandler({ amount, paymentKey, orderId, reason: "OVERSELL" });
      handleValidationModal({ type: "paymentError", content: t("error.server_title"), orderId });
      setIsLoading(false);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyerIdentity = async (cartId?: string) => {
    if (!selectedAddress) {
      setIsCalculationLoading(false);
      return;
    }

    try {
      const { data } = await setBuyerIdentity({
        variables: {
          cartId: cartId ?? cart.id,
          buyerIdentity: {
            customerAccessToken: token.accessToken,
            countryCode: selectedAddress.countryCodeV2 ?? "",
            deliveryAddressPreferences: [
              {
                deliveryAddress: {
                  address1: selectedAddress.address1,
                  address2: selectedAddress.address2,
                  city: selectedAddress.city,
                  company: selectedAddress.company,
                  country: selectedAddress.country,
                  firstName: selectedAddress.firstName,
                  lastName: selectedAddress.lastName,
                  phone: selectedAddress.phone,
                  province: selectedAddress.province,
                  zip: selectedAddress.zip,
                },
              },
            ],
          },
        },
      });

      const updatedCart = data?.cartBuyerIdentityUpdate?.cart;
      // await fetchShippingRates(updatedCart);
      await addDiscountCode(getValues("discountCode"), updatedCart);
    } catch (error) {
      setIsCalculationLoading(false);
      openToast(NOTIFICATIONS.ERROR_SERVER);
      const message = error instanceof Error ? error.message : String(error);
      console.error("[THISISNEVERTHAT INTL] Buyer identity update failed", message);
    }
  };

  /** 관부가세 계산을 위한 Draft Order Calculate API 호출 (할인코드 적용된 상태로 계산) */
  const applyDiscountCode = async (appliedCode: string, providedCart?: ICart["cart"]) => {
    const orderParam = getLambdaPaymentParam(appliedCode, providedCart);
    if (!orderParam) {
      setIsCalculationLoading(false);
      return;
    }

    try {
      setIsCalculationLoading(true);
      // localizedFields 제거
      if (orderParam.orderData.localizedFields) delete orderParam.orderData.localizedFields;
      console.log("orderParam", orderParam);
      const response = await calculateDraftOrderV2({ shop: "thisisneverthat", param: orderParam });
      console.log("[THISISNEVERTHAT INTL] Calculate Draft Order", response?.data);

      if (!response?.data) {
        return false;
      }

      setCalculatedOrder(response.data);

      gtmAddShippingInfo(providedCart ?? cart, response.data, selectedAddress, customer.id ? extractId(customer.id) : undefined);

      if (!appliedCode) {
        handleRemoveDiscountCode();
        return false;
      }

      const userErrors = response?.data?.warnings?.find((warn) => warn.field === "discountCodes");
      const notApplicable = !response?.data?.discountCodes?.length;

      if (userErrors || notApplicable) {
        handleRemoveDiscountCode(t("error.discount_code_title"));
        return false;
      }

      setValue("discountCode", appliedCode);
      setIsDiscountCodeApplied(true);
      return true;
    } catch (error) {
      handleRemoveDiscountCode(t("error.failed_api_discount_code"));
      setIsCalculationLoading(false);
      const message = error instanceof Error ? error.message : String(error);
      console.log(message);
      return false;
    } finally {
      setIsCalculationLoading(false);
    }
  };

  const addDiscountCode = async (appliedCode: string, providedCart?: ICart["cart"]) => {
    setIsDiscountLoading(true);

    // 멤버십 할인코드 사용 유무 확인
    if (appliedCode.toUpperCase() === membership && isMembershipDiscountCodeUsed) {
      handleRemoveDiscountCode(t("error.already_used_discount_code_title"));
      setIsDiscountLoading(false);
      return false;
    }

    const result = await applyDiscountCode(appliedCode, providedCart);
    setIsDiscountLoading(false);
    return result;
  };

  const onSubmit = handleSubmit(async (formData) => {
    console.log("[THISISNEVERTHAT] INTL DISCOUNT CODE SUBMIT", {
      isDiscountCodeApplied,
      discountCodeValue: formData.discountCode,
    });
    if (isDiscountCodeApplied) {
      await applyDiscountCode("");
    } else {
      await addDiscountCode(formData.discountCode.trim());
    }
  });

  const handleRemoveDiscountCode = (errorMessage?: string) => {
    if (errorMessage) {
      setError("discountCode", { type: "manual", message: errorMessage });
    }
    setValue("discountCode", "");
    setIsDiscountCodeApplied(false);
  };

  // 주문생성 API 호출 Parameter 데이터
  const getLambdaPaymentParam = (appliedCode: string, providedCart?: ICart["cart"]) => {
    const billingAddress = isBillingAsShipping ? selectedAddress : (billingAddressRef.current ?? selectedAddress);
    if (!selectedAddress || !billingAddress) return null;

    const cartToUse = providedCart || cart;

    const { countryCode: shippingAddressCountryCode, provinceCode: shippingAddressProvinceCode } = findCodes({
      formData: addressFormData as TIntlAddressForm,
      country: selectedAddress?.country ?? "",
      province: selectedAddress?.province ?? "",
    });

    const { countryCode: billingAddressCountryCode, provinceCode: billingAddressProvinceCode } = findCodes({
      formData: addressFormData as TIntlAddressForm,
      country: billingAddress?.country ?? "",
      province: billingAddress?.province ?? "",
    });

    const selectedAddressCountryCode = selectedAddress?.countryCodeV2 || shippingAddressCountryCode;
    const selectedAddressProvinceCode = selectedAddress?.provinceCode || shippingAddressProvinceCode;

    // 브라질, 중국, 멕시코
    const isNeedResidentId = Object.keys(RESIDENT_COUNTRY).includes(selectedAddressCountryCode);

    const googleTrafficSource = getStoredGoogleTrafficSource();
    const googleCampaignId = getStoredGoogleCampaignId();
    const metaTrafficSource = getStoredMetaTrafficSource();
    const metaCampaignId = getStoredMetaCampaignId();

    return {
      orderData: {
        purchasingEntity: { customerId: customer.id },
        shippingLine: { shippingRateHandle: shippingTitle },
        lineItems: cartToUse.lines.edges.map(({ node }) => ({
          variantId: node.merchandise.id,
          quantity: node.quantity,
        })),
        shippingAddress: {
          firstName: selectedAddress?.firstName || "",
          lastName: selectedAddress?.lastName || "",
          phone: formatPhone(selectedAddress?.phone || ""),
          zip: selectedAddress?.zip || "",
          address1: selectedAddress?.address1 || "",
          address2: selectedAddress?.address2 || "",
          country: selectedAddress?.country || "United States",
          countryCode: selectedAddressCountryCode,
          province: selectedAddress?.province || "",
          provinceCode: selectedAddressProvinceCode,
          city: selectedAddress?.city || "-",
          company: "",
        },
        billingAddress: {
          firstName: billingAddress?.firstName || "",
          lastName: billingAddress?.lastName || "",
          phone: formatPhone(billingAddress?.phone || ""),
          zip: billingAddress?.zip || "",
          address1: billingAddress?.address1 || "",
          address2: billingAddress?.address2 || "",
          country: billingAddress?.country || "United States",
          countryCode: isBillingAsShipping ? selectedAddressCountryCode : billingAddressCountryCode,
          province: billingAddress?.province || "",
          provinceCode: isBillingAsShipping ? selectedAddressProvinceCode : billingAddressProvinceCode,
          city: billingAddress?.city || "-",
          company: "",
        },
        customAttributes: [
          { key: "taxes", value: taxAmount.toFixed(2) },
          { key: "duties", value: dutyAmount.toFixed(2) },
          { key: "shippingFee", value: shippingAmount.toFixed(2) },
          { key: "shippingTitle", value: shippingTitle },
          ...(appliedCode ? [{ key: "DiscountCode", value: appliedCode }] : []),
        ],
        acceptAutomaticDiscounts: true,
        discountCodes: appliedCode ? [appliedCode] : [],
        tags: [
          ...(isEmployee ? ["Employee"] : []),
          ...(appliedCode.toUpperCase() === membership ? ["멤버십_할인코드"] : []),
          ...(googleTrafficSource === "google_ads" ? ["유입_GoogleAds"] : []),
          ...(googleTrafficSource === "google_shopping" ? ["유입_GoogleShopping"] : []),
          ...(googleCampaignId ? [`유입_GoogleAds_${googleCampaignId}`] : []),
          ...(metaTrafficSource === "meta_ads" ? ["유입_MetaAds"] : []),
          ...(metaCampaignId ? [`유입_MetaAds_${metaCampaignId}`] : []),
        ],
        ...(isNeedResidentId && {
          localizedFields: [
            {
              key: RESIDENT_COUNTRY[selectedAddressCountryCode as keyof typeof RESIDENT_COUNTRY].key,
              value: residentId,
            },
          ],
        }),
      },
    };
  };

  const checkBeforePayment = async () => {
    setIsPaymentLoading(true);

    // Get updated customer & Cart
    const { data: updatedCustomer } = await fetchCustomer({ variables: { customerAccessToken: token.accessToken } });
    console.log("[THISISNEVERTHAT INTL] CUSTOMER: ", updatedCustomer);
    if (!updatedCustomer?.customer?.id) {
      setIsPaymentLoading(false);
      openToast(NOTIFICATIONS.WARNING_NO_LOGIN);
      navigate(URLS.HOME);
      return;
    }

    // 카트 재고 체크 및 조정
    const updatedCart = await adjustCartLineItems(cart);
    console.log("[THISISNEVERTHAT INTL] CART: ", updatedCart);
    if (!updatedCart.lines.edges.length) {
      setIsPaymentLoading(false);
      handleValidationModal({ type: "invalidCart", content: t("warning.invalid_cart") });
      return;
    }

    // Check buyerIdentity
    if (extractId(updatedCustomer.customer.id) !== extractId(updatedCart.buyerIdentity.customer?.id || "")) {
      setIsPaymentLoading(false);
      handleValidationModal({ type: "checkoutError", content: t("checkout.wrong_customer_id") });
      return;
    }

    // Check shipping address
    if (!selectedAddress) {
      setIsPaymentLoading(false);
      handleValidationModal({ type: "addressError", content: t("warning.necessary_shipping_address") });
      return;
    }

    // Check valid shipping address key
    if (!isValidAddress(selectedAddress, "en")) {
      setIsPaymentLoading(false);
      handleValidationModal({ type: "addressError", content: t("warning.necessary_invalid_shipping_address") });
      return;
    }

    // Check valid billing address - trigger form validation
    if (!isBillingAsShipping) {
      const isValidBillingAddress = await billingHandleRef.current?.validate();
      if (!isValidBillingAddress) {
        setIsPaymentLoading(false);
        handleValidationModal({
          type: "addressError",
          title: "Billing Address",
          content: t("warning.necessary_billing_address"),
        });
        return;
      }
    }

    // Check Resident Id
    const isValidResidentId = checkResidentId(residentId, selectedAddress);
    setIsAvailableResidentId(isValidResidentId);
    if (!isValidResidentId) {
      setIsPaymentLoading(false);
      const residentName = RESIDENT_COUNTRY[selectedAddress?.countryCodeV2 as keyof typeof RESIDENT_COUNTRY].name;
      handleValidationModal({
        type: "addressError",
        content: `There is a problem with your ${residentName}. Please check and try again.`,
      });
      return;
    }

    const { exceeded, adjustedCartLines } = checkQuantityLimit({
      cart: updatedCart,
      customer: updatedCustomer.customer,
    });

    if (!adjustedCartLines.length) {
      setIsPaymentLoading(false);
      handleValidationModal({ type: "invalidCart", content: t("warning.invalid_cart") });
      return;
    }

    if (exceeded) {
      setIsPaymentLoading(false);
      handleValidationModal({
        type: "overQuantity",
        content: t("warning.quantity_insufficient"),
        cartLines: adjustedCartLines,
      });
      return;
    }

    // Check calculated order
    if (!calculatedOrder) {
      setIsPaymentLoading(false);
      handleValidationModal({ type: "checkoutError", content: t("checkout.wrong_cart_amount") });
      await handleBuyerIdentity(updatedCart.id);
      return;
    }

    // Check product counts
    const cartLineItemTotalAmount = updatedCart.lines.edges.reduce((prev, curr) => prev + parseFloat(curr.node.cost.totalAmount.amount), 0);

    const calculateLineItemTotalAmount = calculatedOrder.lineItems.reduce(
      (prev, next) => prev + parseFloat(next.discountedTotalSet.shopMoney.amount),
      0,
    );

    if (parseFloat(cartLineItemTotalAmount.toFixed(2)) !== parseFloat(calculateLineItemTotalAmount.toFixed(2))) {
      setIsPaymentLoading(false);
      handleValidationModal({ type: "checkoutError", content: t("checkout.wrong_cart_changed") });
      // calculatedOrder 재계산
      await handleBuyerIdentity(updatedCart.id);
      return;
    }

    // Check discount code validity
    const code = calculatedOrder?.discountCodes[0];
    if (code) {
      const isAppliedCode = await addDiscountCode(code);
      if (!isAppliedCode) {
        setIsPaymentLoading(false);
        handleValidationModal({ type: "checkoutError", content: t("checkout.wrong_discount_code") });
        return;
      }
    }

    await requestPaymentWidget(updatedCart);
  };

  /** 카트 재고 체크 및 조정 (재고 부족 시 수량 조정, 품절 상품 제거) */
  const adjustCartLineItems = async (providedCart?: ICart["cart"]) => {
    try {
      const cartToUse = providedCart || cart;
      const cartId = cartToUse.id;
      const { data } = await updateCartLine({
        variables: { cartId, lines: cartToUse.lines.edges.map(({ node }) => ({ id: node.id })) },
      });
      let adjustedCart = data?.cartLinesUpdate.cart || cart;

      if (adjustedCart.lines?.edges.some(({ node }) => node.quantity <= 0)) {
        const readjustedCart = await updateCartLine({
          variables: {
            cartId,
            lines: adjustedCart.lines.edges.map(({ node }) => ({ id: node.id })),
          },
        });
        adjustedCart = readjustedCart?.data?.cartLinesUpdate.cart || adjustedCart;
      }

      return adjustedCart;
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      navigate(URLS.HOME);
      return providedCart ?? cart;
    }
  };

  const handleValidationModal = ({
    type,
    title,
    content,
    cartLines,
    orderId,
  }: {
    type: keyof typeof MODAL_TITLE;
    title?: string;
    content: ReactNode | string;
    cartLines?: CartLineUpdateInput[]; // { id: string; quantity: number }[]
    orderId?: string;
  }) => {
    const callback = async () => {
      try {
        switch (type) {
          case "addressError":
            window.scrollTo(0, 0);
            break;
          case "paymentError":
            // 유효성 검사는 결제 재시도할 때 checkBeforePayment에서 진행하기 때문에 여기서는 결제 위젯 초기화만 진행.
            setSearchParams({});
            clearPaymentData({ orderId: orderId ?? "" });
            await initializeTossWidget();
            break;
          case "overQuantity":
            if (cartLines) await updateCartLine({ variables: { cartId: cart.id, lines: cartLines } });
            // 할인코드, 관부가세, 배송비 등 카트에 변화가 생길 수 있는 요소들에 대해서도 재검증
            await handleBuyerIdentity(cart.id);
            break;
          case "invalidCart":
            await updateCartLine({ variables: { cartId: cart.id, lines: [] } });
            navigate(URLS.HOME);
            break;
          default:
            break;
        }
      } catch (error) {
        openToast(NOTIFICATIONS.ERROR_CHECKOUT);
        navigate(URLS.HOME);
      }
    };

    openModal({
      title: title || MODAL_TITLE[type] || "Checkout Error",
      content,
      confirm: {
        label: "Submit",
        onClick: () => {
          setIsModalLoading(true);
          void callback().finally(() => {
            openModal(null);
            setIsModalLoading(false);
          });
        },
      },
      onClickClose: () => {
        setIsModalLoading(true);
        void callback().finally(() => {
          setIsModalLoading(false);
        });
      },
      isHideCancelButton: true,
    });
  };

  return (
    <>
      {isPaymentWidgetLoading ? <Loading type="page" /> : null}

      <ShippingAddress
        isLoading={setBuyerIdentityLoading || isCalculationLoading}
        selectedAddress={selectedAddress}
        selectedAddressId={selectedAddressId}
        setSelectedAddressId={setSelectedAddressId}
      />

      {/* Resident ID */}
      {selectedAddress?.countryCodeV2 && Object.keys(RESIDENT_COUNTRY).includes(selectedAddress?.countryCodeV2) ? (
        <div className="flex flex-col gap-2.5">
          <h2>Additional Information</h2>
          <div className="flex flex-col items-end gap-2.5">
            <Input
              className="w-full"
              errorMessage={
                residentId && !isAvailableResidentId
                  ? `Enter valid ${RESIDENT_COUNTRY[selectedAddress?.countryCodeV2 as keyof typeof RESIDENT_COUNTRY].name}`
                  : ""
              }
              isError={!isAvailableResidentId}
              onChange={(e) => {
                setResidentId(e.target.value);
                setIsAvailableResidentId(true);
              }}
              placeholder={RESIDENT_COUNTRY[selectedAddress?.countryCodeV2 as keyof typeof RESIDENT_COUNTRY].name}
              value={residentId}
            >
              {residentId ? (
                <Button
                  as="text"
                  className="absolute top-1/2 right-0 -translate-y-1/2"
                  onClick={() => {
                    setResidentId("");
                    setIsAvailableResidentId(true);
                  }}
                >
                  ×
                </Button>
              ) : null}
            </Input>
          </div>
        </div>
      ) : null}

      {/* PAYMENT METHOD */}
      <div className="flex flex-col gap-0.5">
        <h2>Payment Method</h2>
        <div id="payment-method" />
      </div>

      {/* Billing Address */}
      <div className="flex flex-col gap-2.5">
        <h2>Billing Address</h2>
        <Input
          checked={Boolean(isBillingAsShipping)}
          id="billingAddress"
          label={t("checkout.use_as_billing_address")}
          onChange={() => {
            setIsBillingAsShipping((prev) => !prev);
          }}
          type="checkbox"
        />
        {!isBillingAsShipping ? <BillingAddressIntl billingAddressRef={billingAddressRef} ref={billingHandleRef} /> : null}
      </div>

      {/* ORDER SUMMARY */}
      <div className="flex flex-col gap-2.5">
        <h2>Order Summary</h2>
        <div className="tnt-gap-x grid grid-cols-8 gap-y-2.5">
          {/* Products */}
          {cart?.lines.edges
            .filter(({ node }) => !node.merchandise.product.tags.includes("Gift"))
            .map(({ node }) => (
              <CheckoutLineItem key={node.id} node={node} />
            ))}

          {/* Gifts */}
          {cart?.lines.edges
            .filter(({ node }) => node.merchandise.product.tags.includes("Gift"))
            .map(({ node }) => (
              <CheckoutLineItem key={node.id} node={node} />
            ))}
        </div>
      </div>

      {/* DISCOUNT CODE */}
      {!isEmployee && (
        <div className="flex flex-col gap-1">
          <h2>Discount Code</h2>
          {codeUsableMembership && !isMembershipDiscountCodeUsed ? (
            <h3 className="text-blue">{`*Monthly Discount Code: ${membership}`}</h3>
          ) : null}
          <form className="flex flex-col items-end gap-2.5" onSubmit={onSubmit}>
            <Input
              {...register("discountCode", { pattern: /^[a-zA-Z0-9-_]+$/ })}
              {...(errors.discountCode && { isError: true, errorMessage: errors.discountCode?.message })}
              className="w-full"
              disabled={isInitLoading || isDiscountCodeApplied}
              placeholder="Discount Code"
            >
              {isDiscountCodeApplied && !(setBuyerIdentityLoading || isPaymentLoading || isCalculationLoading || isDiscountLoading) ? (
                <Button
                  as="text"
                  className="absolute top-1/2 right-0 -translate-y-1/2"
                  onClick={() => {
                    isDiscountCodeApplied ? onSubmit() : reset();
                  }}
                >
                  {isDiscountCodeApplied ? "Remove" : "×"}
                </Button>
              ) : null}
            </Input>
            {isDiscountCodeApplied ? <p className="text-xs font-normal">{t("checkout.discount_code_alert_description")}</p> : null}
            <Button
              as="square"
              disabled={isCalculationLoading || (!isDiscountCodeApplied && (!discountCode || Boolean(errors.discountCode)))}
              loading={isDiscountLoading}
              type="submit"
              variant="secondary"
            >
              {isDiscountCodeApplied ? t("submitted") : t("submit")}
            </Button>
          </form>
        </div>
      )}

      {/* Notice */}
      <div className="flex flex-col gap-2.5">
        <h2>Important Note</h2>
        <p>{t("checkout.notice")}</p>
      </div>

      {/* SUMMARY*/}
      <div className="pc:pb-24 flex flex-col justify-end gap-2 gap-y-2.5 pb-12">
        <div className="flex flex-col gap-1">
          <div className="tnt-gap-x grid grid-cols-2">
            <span>Subtotal</span>
            <Price className="justify-end" price={originTotalAmount} />
          </div>
          <div className="tnt-gap-x grid grid-cols-2">
            <span className="w-fit">{shippingTitle}</span>
            {setBuyerIdentityLoading || isCalculationLoading ? (
              <span className="flex justify-end">Calculating...</span>
            ) : (
              <Price className="justify-end" price={shippingAmount} />
            )}
          </div>

          <div className="tnt-gap-x grid grid-cols-2">
            <span>Duties</span>
            {(setBuyerIdentityLoading || isCalculationLoading) && !isPaymentLoading ? (
              <span className="flex justify-end">Calculating...</span>
            ) : (
              <Price className="justify-end" price={dutyAmount} />
            )}
          </div>

          <div className="tnt-gap-x grid grid-cols-2">
            <span>Taxes</span>
            {(setBuyerIdentityLoading || isCalculationLoading) && !isPaymentLoading ? (
              <span className="flex justify-end">Calculating...</span>
            ) : (
              <Price className="justify-end" price={taxAmount} />
            )}
          </div>

          <Discounts
            autoDiscountAmount={autoDiscountAmount}
            autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
            codeDiscountAmount={codeDiscountAmount}
            codeDiscountTitle={codeDiscountTitle}
          />

          <div className="tnt-gap-x grid grid-cols-2">
            <span>Total</span>
            <Price className="justify-end" price={totalAmount} />
          </div>
        </div>
        <Button
          as="square"
          className="h-11.5! w-full justify-center"
          disabled={
            isInitLoading ||
            isCalculationLoading ||
            isPaymentLoading ||
            isDiscountLoading ||
            setBuyerIdentityLoading ||
            updateCartLineLoading ||
            limitedProductIds.length > 0
          }
          loading={
            isInitLoading ||
            isCalculationLoading ||
            isPaymentLoading ||
            isDiscountLoading ||
            setBuyerIdentityLoading ||
            updateCartLineLoading
          }
          onClick={checkBeforePayment}
        >
          {t("checkout.pay_now")}
        </Button>
      </div>
    </>
  );
}
